import React, { useEffect, useRef, useState } from "react";

import { Drawer } from "antd";
import PDFMerger from "pdf-merger-js/browser";
import { toast } from "react-toastify";

import HorizontalScroll from "../../../../Common/HorizontalScroll/HorizontalScroll";
import { fetchCoaPreviewFromSample } from "../../../../Common/Template/coaPreviewHelper";
import { COA_NAME_INVALID_CHARS_REGEX } from "../../../../Common/Template/helper";
import { TEMPLATE_SIDES } from "../../../../Common/Template/TemplateContext";
import PdfPreviewModal from "../../../../Common/UIComponents/PdfPreviewModal";
import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";
import { flattenCoaFilePaths } from "../ReportSpecs/helper";
import CoaTemplatePanel, { DEFAULT_COA_TEMPLATE_ID } from "./CoaTemplatePanel";
import PreviewDatePicker from "./PreviewDatePicker";

import { downloadFileFromS3WithPresigned, getFileFromS3WithPresigned } from "../../../../../utils/helpers";
import { canAccessTemplates } from "../../../../../utils/Permissions";

import { ReactComponent as CoaTemplateTabIcon } from "../../../../../assets/images/product/selectTemplate.svg";

import "./CoaPreviewModal.css";

const createPreviewItem = (fileBlob, fileName, extra = {}) => {
  const url = window.URL.createObjectURL(fileBlob);
  return {
    url,
    blob: fileBlob,
    name: fileName,
    ...extra,
  };
};

const downloadPdfBlob = (fileBlob, fileName) => {
  const downloadUrl = window.URL.createObjectURL(fileBlob);
  const downloadLink = document.createElement("a");
  downloadLink.href = downloadUrl;
  downloadLink.download = fileName;
  downloadLink.click();
  downloadLink.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

const getFileNameFromS3Path = (filePath) => {
  if (!filePath || typeof filePath !== "string") {
    return "";
  }
  return filePath.split("/").pop() || "";
};

const stripPdfExtension = (fileName) => String(fileName || "").replace(/\.pdf$/i, "");

const sanitizeFileNamePart = (value) => (
  String(value || "").trim().replace(COA_NAME_INVALID_CHARS_REGEX, "")
);

const buildTemplateCoaFileName = ({ originalFileName, templateName, sampleId }) => {
  const originalStem = stripPdfExtension(originalFileName)
    || sanitizeFileNamePart(sampleId)
    || "COA";
  const templateStem = sanitizeFileNamePart(templateName);
  if (!templateStem) {
    return `${originalStem}.pdf`;
  }
  return `${originalStem}_${templateStem}.pdf`;
};

const DEFAULT_PREVIEW_CATEGORY = "report";
const PREVIEW_TAB_SEPARATOR = "::";

const buildPreviewTabKey = (date, category) => `${date}${PREVIEW_TAB_SEPARATOR}${category}`;

const normalizePreviewFilePaths = (filePaths) => {
  if (!filePaths || typeof filePaths !== "object") {
    return {};
  }

  return Object.fromEntries(
    Object.entries(filePaths).map(([date, dateData]) => (
      Array.isArray(dateData)
        ? [date, { [DEFAULT_PREVIEW_CATEGORY]: dateData }]
        : [date, dateData]
    )),
  );
};

const getSortedFileEntries = (filePaths) => (
  Object.entries(filePaths || {}).sort((firstEntry, secondEntry) => (
    Date.parse(secondEntry[0]) - Date.parse(firstEntry[0])
  ))
);

const buildCategoriesByDate = (fileEntries) => fileEntries.reduce((categoriesByDate, entry) => {
  categoriesByDate[entry[0]] = Object.keys(entry[1]);
  return categoriesByDate;
}, {});

const getFileFromAws = async (filePath) => {
  const isPrivateS3Bucket = filePath.includes("COAs/");
  const fileName = filePath.split("/").pop();
  const fileBlobObj = await getFileFromS3WithPresigned(
    [filePath],
    isPrivateS3Bucket ? "private" : "public",
  );
  if (fileBlobObj?.[filePath]) {
    return { fileName, file: fileBlobObj[filePath] };
  }
  return null;
};

const mergePdfs = async (fileBlobs, date, category) => {
  if (!fileBlobs.length) {
    return { url: "", date, category };
  }

  if (fileBlobs.length === 1) {
    const { fileName, file } = fileBlobs[0];
    return createPreviewItem(file, fileName, { date, category });
  }

  const merger = new PDFMerger();
  const fileNames = fileBlobs.map(({ fileName }) => fileName);
  await Promise.all(fileBlobs.map(({ file }) => merger.add(file)));
  const mergedPdf = await merger.saveAsBlob();
  return createPreviewItem(mergedPdf, fileNames.join(), { date, category });
};

const loadOriginalPreviewFiles = async (filePaths) => {
  if (!filePaths || Object.keys(filePaths).length < 1) {
    throw new Error("Unable to preview, no valid file path for this report!");
  }

  const filesByDateAndCategory = getSortedFileEntries(filePaths).flatMap(([date, data]) => (
    Object.entries(data).map((categoryEntry) => [date, ...categoryEntry])
  ));

  return Promise.all(filesByDateAndCategory.map(async ([date, category, filepaths]) => {
    const uniqueFilePaths = Array.from(new Set(filepaths));
    const fetchedFiles = (await Promise.all(uniqueFilePaths.map(getFileFromAws)))
      .filter((file) => file?.file);
    if (!fetchedFiles.length) {
      throw new Error("Failed to fetch files");
    }
    return mergePdfs(fetchedFiles, date, category);
  }));
};

const CoaPreviewModal = ({
  filePaths,
  report,
  handleCloseForPreview,
  showDownloadButton = true,
  side = TEMPLATE_SIDES.PRODUCT,
  loadTemplatePreview,
  zIndex = 1000,
}) => {
  const previewFilePaths = normalizePreviewFilePaths(filePaths);
  const fileEntries = getSortedFileEntries(previewFilePaths);
  const categories = buildCategoriesByDate(fileEntries);
  const reportDates = fileEntries.map((entry) => ({ date: entry[0] }));
  const originalFilePaths = flattenCoaFilePaths(previewFilePaths);

  const [loadingFiles, setLoadingFiles] = useState(true);
  const [previewUrlList, setPreviewUrlList] = useState([{ url: null }]);
  const [activeDate, setActiveDate] = useState(fileEntries[0]?.[0] || null);
  const [selectedCategory, setSelectedCategory] = useState(
    fileEntries[0] ? categories[fileEntries[0][0]][0] : null,
  );
  const [downloadingCoa, setDownloadingCoa] = useState(false);
  const [isTemplatePanelOpen, setIsTemplatePanelOpen] = useState(false);
  const [isTemplatePreviewActive, setIsTemplatePreviewActive] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(DEFAULT_COA_TEMPLATE_ID);
  const objectUrlsRef = useRef([]);
  const originalPreviewFilesRef = useRef([]);
  const originalLoadSettledRef = useRef(false);
  const isTemplatePreviewActiveRef = useRef(false);
  const templateAbortRef = useRef(null);

  const trackPreviewUrl = (previewItem) => {
    if (previewItem?.url) {
      objectUrlsRef.current.push(previewItem.url);
    }
    return previewItem;
  };

  const getOriginalCoaFileName = () => {
    const activeDateFiles = previewFilePaths[activeDate];
    if (activeDateFiles && typeof activeDateFiles === "object") {
      const firstPath = Object.values(activeDateFiles)
        .find((fileList) => Array.isArray(fileList) && fileList.length)?.[0];
      const activeDateFileName = getFileNameFromS3Path(firstPath);
      if (activeDateFileName) {
        return activeDateFileName;
      }
    }
    return getFileNameFromS3Path(originalFilePaths[0]);
  };

  const resolveTemplatePreviewFileName = (template) => (
    buildTemplateCoaFileName({
      originalFileName: getOriginalCoaFileName(),
      templateName: template.name,
      sampleId: report?.sample_id,
    })
  );

  useEffect(() => {
    let isCancelled = false;
    const objectUrls = objectUrlsRef.current;

    const loadOriginalCoa = async () => {
      try {
        const mergedPreviewFiles = await loadOriginalPreviewFiles(previewFilePaths);
        mergedPreviewFiles.forEach(trackPreviewUrl);
        originalPreviewFilesRef.current = mergedPreviewFiles;
        originalLoadSettledRef.current = true;
        if (isCancelled || isTemplatePreviewActiveRef.current) {
          return;
        }

        setPreviewUrlList(mergedPreviewFiles);
        setLoadingFiles(false);
      } catch (error) {
        originalLoadSettledRef.current = true;
        if (isCancelled || isTemplatePreviewActiveRef.current) {
          return;
        }
        setLoadingFiles(false);
        toast.error(error.message || "Preview not available.");
      }
    };

    loadOriginalCoa();

    return () => {
      isCancelled = true;
      if (templateAbortRef.current) {
        templateAbortRef.current.abort();
      }
      objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateChanged = (date) => {
    const category = categories[date][0];
    setActiveDate(date);
    setSelectedCategory(category);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleDownloadCoa = async () => {
    const templatePreview = previewUrlList[0];
    if (isTemplatePreviewActive && templatePreview?.blob) {
      try {
        downloadPdfBlob(templatePreview.blob, templatePreview.name);
        toast.success("Successfully downloaded COA.");
      } catch (error) {
        toast.error(error.message || "Failed to download COA");
      }
      return;
    }

    if (originalFilePaths.length === 0) {
      toast.error("Unable to download, no valid file path for this report!");
      return;
    }

    setDownloadingCoa(true);

    try {
      const downloadResults = await Promise.allSettled(
        originalFilePaths.map((filePath) => {
          const fileName = filePath.split("/").pop() || "";
          const isPrivateS3Bucket = filePath.includes("COAs/");
          return downloadFileFromS3WithPresigned(
            [filePath],
            isPrivateS3Bucket ? "private" : "public",
            [fileName],
          );
        }),
      );

      const successCount = downloadResults.filter(
        (result) => result.status === "fulfilled" && result.value,
      ).length;

      if (successCount === originalFilePaths.length) {
        toast.success(`Successfully downloaded ${successCount}/${originalFilePaths.length} files.`);
      } else if (successCount > 0) {
        toast.warning(`Downloaded ${successCount}/${originalFilePaths.length} files.`);
      } else {
        toast.error("Failed to download COA");
      }
    } catch (error) {
      toast.error(error.message || "Failed to download COA");
    } finally {
      setDownloadingCoa(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    if (!template.template_id) {
      return;
    }

    if (template.isDefault || String(template.template_id) === String(DEFAULT_COA_TEMPLATE_ID)) {
      if (templateAbortRef.current) {
        templateAbortRef.current.abort();
      }
      setSelectedTemplateId(DEFAULT_COA_TEMPLATE_ID);
      isTemplatePreviewActiveRef.current = false;
      setIsTemplatePreviewActive(false);

      const firstDate = fileEntries[0]?.[0] || null;
      const firstCategory = firstDate ? categories[firstDate][0] : null;
      setActiveDate(firstDate);
      setSelectedCategory(firstCategory);

      const originalPreviewFiles = originalPreviewFilesRef.current;
      if (originalPreviewFiles.length) {
        setPreviewUrlList(originalPreviewFiles);
        setLoadingFiles(false);
      } else if (originalLoadSettledRef.current) {
        setPreviewUrlList([{ url: null }]);
        setLoadingFiles(false);
      }
      return;
    }

    const previousTemplateId = selectedTemplateId;
    setSelectedTemplateId(template.template_id);

    if (templateAbortRef.current) {
      templateAbortRef.current.abort();
    }
    const abortController = new AbortController();
    templateAbortRef.current = abortController;
    setLoadingFiles(true);

    try {
      const previewResult = loadTemplatePreview
        ? await loadTemplatePreview(template, abortController.signal)
        : await fetchCoaPreviewFromSample({
          template_id: template.template_id,
          sample_unique_identifier: report.sample_unique_identifier,
          sample_id: report.sample_id,
          sample_type: report.sample_type,
          received_date: report.received_date,
          side,
        }, abortController.signal);

      if (previewResult.canceled || abortController.signal.aborted) {
        return;
      }
      if (previewResult.success === false) {
        setSelectedTemplateId(previousTemplateId);
        toast.error(previewResult.message);
        return;
      }

      const previewItem = trackPreviewUrl(createPreviewItem(
        previewResult.blob,
        resolveTemplatePreviewFileName(template),
      ));
      isTemplatePreviewActiveRef.current = true;
      setPreviewUrlList([previewItem]);
      setIsTemplatePreviewActive(true);
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }
      setSelectedTemplateId(previousTemplateId);
      toast.error(error.message);
    } finally {
      if (!abortController.signal.aborted) {
        setLoadingFiles(false);
      }
    }
  };

  const canDownload = isTemplatePreviewActive
    ? Boolean(previewUrlList[0]?.blob)
    : originalFilePaths.length > 0;
  const canChooseTemplates = canAccessTemplates(side);
  const hasMultipleDates = !isTemplatePreviewActive && reportDates.length > 1;
  const dateCategories = (
    !isTemplatePreviewActive && activeDate && categories[activeDate]
  ) || [];
  const hasMultipleCategories = !isTemplatePreviewActive
    && !loadingFiles
    && dateCategories.length > 1;

  const activePreviewItem = (() => {
    if (isTemplatePreviewActive) {
      return previewUrlList[0] || {};
    }
    if (!dateCategories.length) {
      return previewUrlList[0] || {};
    }
    return previewUrlList.find(
      (file) => file.date === activeDate && file.category === selectedCategory,
    ) || {};
  })();

  const previewFiles = [{
    key: isTemplatePreviewActive
      ? "template"
      : buildPreviewTabKey(activeDate, selectedCategory || "coa"),
    fileName: activePreviewItem.name,
    src: activePreviewItem.url,
  }];

  const headerExtraContent = (
    <>
      {hasMultipleDates && (
        <PreviewDatePicker
          data={reportDates}
          activeDate={activeDate}
          updateActiveDate={handleDateChanged}
        />
      )}
      {hasMultipleCategories && (
        <HorizontalScroll containerClassName="PdfPreviewModal__CategoryList">
          {dateCategories.map((category) => (
            <span
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={[
                "PdfPreviewModal__CategoryListItem",
                category === selectedCategory
                  ? "PdfPreviewModal__CategoryListItem--selected"
                  : "",
              ].filter(Boolean).join(" ")}
            >
              {category}
            </span>
          ))}
        </HorizontalScroll>
      )}
    </>
  );

  return (
    <PdfPreviewModal
      open
      title="COA"
      onClose={handleCloseForPreview}
      files={previewFiles}
      loading={loadingFiles}
      showDownload={showDownloadButton && canDownload}
      showPrint={false}
      downloadLabel="Download COA"
      downloadLoading={downloadingCoa}
      downloadDisabled={!canDownload || downloadingCoa}
      onDownload={handleDownloadCoa}
      errorMessage="Preview not available."
      width="40vw"
      zIndex={zIndex}
      className={isTemplatePanelOpen ? "PdfPreviewModal--panelOpen" : ""}
      headerExtra={(hasMultipleDates || hasMultipleCategories) ? headerExtraContent : null}
      extra={(
        <>
          {canChooseTemplates && !isTemplatePanelOpen && (
            <div className="CoaPreviewModal__TemplateTabRail">
              <StyledTooltip
                title="Choose from your created templates for the report."
                placement="left"
                className="CoaPreviewModal__TemplateHint"
              >
                <button
                  type="button"
                  className="CoaPreviewModal__TemplateTab"
                  aria-label="Open templates panel"
                  onClick={() => setIsTemplatePanelOpen(true)}
                >
                  <CoaTemplateTabIcon className="CoaPreviewModal__TemplateTabIcon" aria-hidden />
                </button>
              </StyledTooltip>
            </div>
          )}
          {canChooseTemplates && (
            <Drawer
              open={isTemplatePanelOpen}
              onClose={() => setIsTemplatePanelOpen(false)}
              placement="right"
              mask={false}
              getContainer={false}
              rootClassName="CoaPreviewModal__TemplateDrawer"
            >
              <CoaTemplatePanel
                selectedTemplateId={selectedTemplateId}
                onSelectTemplate={handleSelectTemplate}
                onClosePanel={() => setIsTemplatePanelOpen(false)}
                side={side}
              />
            </Drawer>
          )}
        </>
      )}
    />
  );
};

export default CoaPreviewModal;
